import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { cloneDeep, debounce } from 'lodash';
import { Subject } from 'rxjs';
import { EntityList, EntityListItem } from 'weavix-shared/models/entity-list.model';
import { FilterResultType, MapTableMode } from 'weavix-shared/models/weavix-map.model';
import { AutoUnsubscribe, Utils } from 'weavix-shared/utils/utils';
import { FilterType } from 'components/data-filter/data-filter.model';
import { MapFeatures } from '../simple-map.component';
import { CommonModule } from '@angular/common';
import { InputComponent } from 'components/input/input.component';
import { EntityListComponent } from 'components/entity-list/entity-list.component';
import { TranslateModule } from '@ngx-translate/core';
import { ClickOutsideDirective } from 'weavix-shared/directives/click-outside.directive';
import { FacilitySwitchService } from 'weavix-shared/services/facility-switch.service';
import { FacilityService } from 'weavix-shared/services/facility.service';

interface SimpleMapCount { showing: number, total: number }

// Note: do not imitate this pattern of global exported subjects!
export const handleMapSearchResultClick$ = new Subject<EntityListItem>();
export const updateSimpleMapCountDisplay = new Subject<SimpleMapCount>();

@AutoUnsubscribe()
@Component({
    selector: 'app-simple-map-search',
    templateUrl: './simple-map-search.component.html',
    styleUrls: ['./simple-map-search.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        TranslateModule,

        ClickOutsideDirective,
        InputComponent,
        EntityListComponent,
    ],
})
export class SimpleMapSearchComponent implements OnInit {
    @Input() searchConfig: MapFeatures['search'];
    @Input() activeMapTableMode: MapTableMode;
    @Output() searchQueryOutput = new EventEmitter<string>();

    searchResults: EntityList;
    showResults: boolean = false;
    searchQuery: string;
    filteredMarkerCount: SimpleMapCount;
    debounceSearch = debounce((value: string) => this.applySearch(value), 500);

    constructor(
        private readonly facilitySwitchService: FacilitySwitchService,
        private readonly facilityService: FacilityService,
    ) { }

    get noResults(): boolean { return Object.values(this.searchResults).every(v => !v.length); }

    ngOnInit() {
        Utils.safeSubscribe(this, updateSimpleMapCountDisplay).subscribe(res => this.filteredMarkerCount = res);
    }

    handleSearch(value: string): void {
        this.debounceSearch(value);
    }

    private setSearchQuery(value: string): void {
        this.searchQuery = value?.length ? value : null;
    }

    private applySearch(value: string): void {
        this.setSearchQuery(value);
        if (this.activeMapTableMode === MapTableMode.Map) this.applyMapSearch(value);
        else this.searchQueryOutput.emit(value);
    }

    private applyMapSearch(query: string): void {
        if (this.searchQuery) {
            this.setSearchResults(query);

            if (!this.noResults) this.showResultsList();
            else this.hideResultsList();
        } else {
            this.clearSearch();
        }
    }

    private setSearchResults(searchValue: string): void {
        this.searchResults = {};

        Object.keys(this.searchConfig.types).forEach(type => {
            switch (type) {
                case FilterResultType.Person : {
                    const peopleResults = this.searchPeople(searchValue);
                    this.searchResults[FilterResultType.Person] = peopleResults;
                    break;
                }
                case FilterType.Geofence : {
                    const geoResults = this.searchGeofences(searchValue);
                    this.searchResults[FilterResultType.Geofence] = geoResults;
                    break;
                }
                case FilterType.Walt : {
                    const waltResults = this.searchWalts(searchValue);
                    this.searchResults[FilterResultType.Walt] = waltResults;
                    break;
                }
            }
        });
    }

    private searchPeople(searchValue: string): EntityListItem[] {
        const people = this.searchConfig.types.person();
        return people
            .filter(p => p.fullName.toLowerCase().includes(searchValue.toLowerCase()))
            .map(p => ({ id: p.id, type: FilterResultType.Person, name: p.fullName, entity: p }));
    }

    private searchGeofences(searchValue: string): EntityListItem[] {
        const geofences = this.searchConfig.types.geofence() ?? [];
        return geofences
            .filter(g => g.name.toLowerCase().includes(searchValue.toLowerCase()))
            .map(g => ({ id: g.id, type: FilterResultType.Geofence, name: g.name, entity: g }));
    }

    private searchWalts(searchValue: string): EntityListItem[] {
        const walts = this.searchConfig.types.walt() ?? [];
        return walts
            .filter(w => w.id.toLowerCase().includes(searchValue.toLowerCase()) || w.person?.fullName?.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase()))
            .map(w => ({ id: w.id, type: FilterResultType.Walt, name: w.id, entity: w }));
    }

    showResultsList(): void {
        this.showResults = true;
    }

    hideResultsList(): void {
        this.showResults = false;
    }

    async handleItemClick(item: EntityListItem): Promise<void> {
        this.hideResultsList();

        const facilities = await this.facilityService.getMyFacilitiesOnCurrentAccount(this);
        const badgeFacilities = item?.entity?.['badge']?.facilities?.map(f => f.id) ?? [];
        const targetFacility = facilities.find(facility => badgeFacilities.includes(facility.id));

        if (targetFacility) {
            const selectedItem = cloneDeep(item);
            delete selectedItem.entity['marker'];
            delete selectedItem.entity['listener'];
            delete selectedItem.entity['closeListener'];
            sessionStorage.setItem('selectedEntityListItem', JSON.stringify(selectedItem));

            if (this.facilityService.getCurrentFacility()?.id !== targetFacility.id) {
                this.facilityService.setCurrentFacility(targetFacility);
                this.facilitySwitchService.switchFacility(targetFacility, true);
            }
        }

        handleMapSearchResultClick$.next(item);
    }

    public clearSearch(): void {
        this.hideResultsList();
        this.setSearchQuery(null);
    }
}
